import React from 'react';
import { Button, Typography, Box } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary for TechDocs pages. Catches metadata fetch failures
 * (e.g. blob not found in Azure Blob Storage) and shows a friendly
 * message instead of infinite loading or crashing the app.
 */
export class TechDocsErrorBoundary extends React.Component<Props, State> {
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message ?? '';
      const isBlobMissing =
        message.includes('blob does not exist') ||
        message.includes('metadata fetch failed') ||
        message.includes('404');

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
          textAlign="center"
          p={4}
        >
          <DescriptionIcon
            style={{ fontSize: 64, color: '#94a3b8', marginBottom: 16 }}
          />
          <Typography variant="h5" gutterBottom>
            Documentation not available
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ maxWidth: 480, marginBottom: 24 }}
          >
            {isBlobMissing
              ? 'The documentation for this component has not been generated yet. It may need to be built and published first.'
              : 'An error occurred while loading the documentation.'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => window.history.back()}
          >
            Go back
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
